import { useState } from "react";
import { toast } from "sonner";
import {
  Presentation, Download, FileText, ExternalLink, Link2, Calendar, Layers, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Decks staged for customer meetings. Files live in /public/downloads so they are
// shareable by link without a login.
const DECKS = [
  {
    id: "north-houston-speaker",
    title: "North Houston District — Development Block Speaker Invitation",
    audience: "Lincoln Property Company & E.E. Reed · no sponsorship ask",
    date: "Thursday, Jan 14, 2027 · Hilton Houston North",
    slides: 13,
    cover: "/downloads/north_houston_speaker_cover.jpg",
    pptx: "/downloads/Sterile_Mate_North_Houston_Development_Speaker_Invitation.pptx",
    pdf: "/downloads/Sterile_Mate_North_Houston_Development_Speaker_Invitation.pdf",
    summary:
      "Sponsorship-free variant of the event deck, built to invite the developer "
      + "and the prime to present the Greenspoint Mall demolition and City North "
      + "Logistics Park to City- and County-registered vendors. No packages, no "
      + "event budget, no dollar asks anywhere in the file.",
    highlights: [
      "Greenspoint Mall Development Block — Lincoln Property and E.E. Reed featured back to back, 15 minutes each",
      "Run of show and presentation lineup follow the sponsorship deck; the featured block opens the procurement presentations",
      "Project framing: $150M development with a $15M City of Houston contribution",
      "Built to the City of Houston's small business participation guidelines (no MBE/WBE framing)",
      "Benefit pages: prequalified bench before bid day, shorter mobilization, credit for opening the bid list",
      "Dedicated page on the attendee contact list Sterile Mate builds and delivers to both companies",
    ],
  },
  {
    id: "north-houston-sponsorship",
    title: "North Houston District — Sponsorship & Partnership Deck",
    audience: "Four sponsorship positions · outreach underway",
    date: "Thursday, Jan 14, 2027 · Hilton Houston North",
    slides: 14,
    cover: "/downloads/north_houston_sponsorship_cover.jpg",
    pptx: "/downloads/Sterile_Mate_North_Houston_District_Sponsorship_Deck.pptx",
    pdf: "/downloads/Sterile_Mate_North_Houston_District_Sponsorship_Deck.pdf",
    summary:
      "Sponsor-ready deck for \"Growth, Opportunity & Business Connections\" — "
      + "presented by Harris Health System, hosted by Harris County, curated by "
      + "Sterile Mate and the Greater Houston Procurement Forum. Three unnamed "
      + "packages underwrite $8,548.00 of catering and stage production.",
    highlights: [
      "The ask, costed: breakfast $1,624.50 · box lunch $5,479.50 · stage & AV $1,444.00 — $8,548.00 total",
      "Box lunch split into two equal co-sponsorships at $2,739.75 each — four positions in total",
      "Lunch co-sponsors get the longest speaking slot of the day (20 min); everyone else gets 15",
      "Procurement opportunity block replaces the bus tour: Kiewit, Tellepsen, Harris Health, East Water Plant Phase 2, North Houston District, METRO",
      "Outreach noted: Frost Bank, Kiewit, North Houston District and Tellepsen (Tellepsen already green-lit)",
    ],
  },
  {
    id: "harris-health-6yr",
    title: "Harris Health System — 6 Years of Partnership",
    audience: "Lyndon B. Johnson Hospital · Supply Chain Week 2026",
    date: "October 2026",
    slides: 17,
    cover: "/downloads/harris_health_deck_cover.jpg",
    pptx: "/downloads/Sterile_Mate_Harris_Health_6_Year_Partnership.pptx",
    pdf: "/downloads/Sterile_Mate_Harris_Health_6_Year_Partnership.pdf",
    summary:
      "Supply Chain Week thank-you deck celebrating six years as Harris Health's "
      + "backorder and hard-to-find supplier, with a deep dive on the automated "
      + "backorder tool.",
    highlights: [
      "Partnership story — trust that opened doors, including the critical water projects",
      "Prime lines: American Surgical neuro sponges · Sol Millennium needles, syringes, medication delivery, diabetes care",
      "Automated backorder tool: 5-step flow with live screenshots of the request screen, Sourcing Desk, Build Quote and fulfillment tracking",
      "Live backorder availability list · AskIsaac on Signal · lead times cut up to 50%",
      "Closing QR code that opens the customer-facing backorder request screen",
    ],
  },
];


const DeckCard = ({ deck }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const url = `${window.location.origin}${deck.pptx}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy — long-press the download button instead");
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`deck-card-${deck.id}`}
    >
      <div className="grid md:grid-cols-[22rem_1fr]">
        <a
          href={deck.pdf}
          target="_blank"
          rel="noreferrer"
          className="block bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 group"
          title="Open the PDF version"
          data-testid={`deck-cover-${deck.id}`}
        >
          <img
            src={deck.cover}
            alt={`${deck.title} cover slide`}
            className="w-full h-auto object-cover group-hover:opacity-95 transition-opacity"
          />
        </a>

        <div className="p-5">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-cyan-700 mb-1.5">
            <span className="inline-flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> {deck.audience}
            </span>
          </div>
          <h2 className="text-lg md:text-lg font-bold text-slate-900">{deck.title}</h2>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{deck.summary}</p>

          <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> {deck.slides} slides
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {deck.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Presentation className="w-3.5 h-3.5" /> PowerPoint · editable
            </span>
          </div>

          <ul className="mt-4 space-y-1.5">
            {deck.highlights.map((h) => (
              <li key={h} className="flex gap-2 text-[13px] text-slate-600">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 mt-5">
            <Button asChild size="sm" className="gap-1.5 bg-slate-900 hover:bg-slate-800">
              <a href={deck.pptx} download data-testid={`deck-download-pptx-${deck.id}`}>
                <Download className="w-3.5 h-3.5" /> Download .pptx
              </a>
            </Button>
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <a href={deck.pdf} target="_blank" rel="noreferrer"
                 data-testid={`deck-open-pdf-${deck.id}`}>
                <FileText className="w-3.5 h-3.5" /> Open PDF
              </a>
            </Button>
            <Button size="sm" variant="outline" onClick={copyLink} className="gap-1.5"
                    data-testid={`deck-copy-link-${deck.id}`}>
              <Link2 className="w-3.5 h-3.5" /> {copied ? "Link copied" : "Copy share link"}
            </Button>
            {deck.id === "harris-health-6yr" && (
              <Button asChild size="sm" variant="ghost" className="gap-1.5 text-slate-600">
                <a href="/#quick-quote" target="_blank" rel="noreferrer"
                   data-testid={`deck-open-request-${deck.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> Backorder request screen
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default function DemoPresentationsPage() {
  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="demo-presentations-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Presentation className="w-6 h-6 text-cyan-600" /> Demo Presentations
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Customer-ready decks staged for meetings. Download the PowerPoint to edit, send
          the PDF, or copy a share link — no login needed on the recipient's side.
        </p>
      </div>

      {DECKS.map((d) => <DeckCard key={d.id} deck={d} />)}
    </div>
  );
}
